// Import statements
import { ErrorBoundary } from "@sentry/react";
import DocsBotAIWidget from "component/DocsBotAIWidget/DocsBotAIWidget";
import {
  ConfigCatProvider,
  User as ConfigCatUser,
  PollingMode,
  useFeatureFlag,
} from "configcat-react";
import { useAppDispatch, useAppSelector } from "hook/redux";
import AuthLayout from "layout/AuthLayout";
import React, { useEffect, useMemo } from "react";
import { Provider } from "react-redux";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import * as routes from "routes";
import VersionService from "service/Version.service";
import { setMode, setRavenVersionInfo, setOrg } from "slice/CommonSlice";
import { setFlag } from "slice/ConfigCatSlice";
import { ConfigCatFeatureFlag, Themes } from "util/CommonConstant";
import {
  getEnvVariableValue,
  getLocalStorage,
  setLocalStorage,
  isValidHttpUrl,
} from "util/CommonUtil";
import { ENV_VAR } from "util/Constant";
import PageForgotPassword from "view/PageForgotPassword/PageForgotPassword";
import PageJoinToday from "view/PageJoinToday/PageJoinToday";
import PageResetPassword from "view/PageResetPassword/PageResetPassword";
import PageSignUp from "view/PageSignUp/PageSignUp";
import Loader from "./component/Loader";
import store, { AppDispatch, persistor } from "./store";
import AxiosService from "service/Axios.service";
import OrgSpec from "model/api/OrgSpec.model";
import OrgService from "service/Org.service";
import useFavicon from "hook/useFavicon";
import useTitleChange from "hook/useTitleChange";
import useIsWhitelabelled from "hook/useIsWhitelabelled";
// Containers
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));
const PageAppCrash = React.lazy(() => import("./view/PageAppCrash"));

// Pages
const Page404 = React.lazy(() => import("./view/Page404"));
const PageLogin = React.lazy(() => import("./view/PageLogin"));
const BaseAPIChanger = React.lazy(() => import("./view/BaseAPIChanger"));
const PageVersions = React.lazy(() => import("./view/PageVersions"));
const PageShareLink = React.lazy(() => import("./view/redirection/PageShareLink/PageShareLink"));

const SDK_KEY = getEnvVariableValue(ENV_VAR.VITE_CONFIGCAT_SDK_KEY) as string;
const VITE_CONFIGCAT_POLL_INTERVAL = getEnvVariableValue(
  ENV_VAR.VITE_CONFIGCAT_POLL_INTERVAL
) as string;

const baseUrl = AxiosService.getDefaultApiUrl(true);

//Subdomain without the protocol and port
const subDomain = baseUrl.split(".")[0].split("//")[1] || "development";

const defaultUser: ConfigCatUser = {
  identifier: subDomain,
  email: "not-logged-in-user",
  custom: {
    baseUrl: AxiosService.getDefaultApiUrl(true),
    browserOrigin: window.location.origin,
    org_name: getEnvVariableValue(ENV_VAR.VITE_ORG_NAME),
    cluster: getEnvVariableValue(ENV_VAR.VITE_CLUSTER),
    user_role: "guest",
    utcTime: new Date().toISOString(),
    localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    localTime: new Date().toString(),
    timeOffset: new Date().getTimezoneOffset(),
    userAgent: navigator.userAgent,
  },
};
console.log("defaultUser", defaultUser);

// App component
function App(props: any) {
  return (
    <React.Suspense fallback={<Loader />}>
      <ErrorBoundary fallback={<PageAppCrash />}>
        <ConfigCatProvider
          sdkKey={SDK_KEY}
          pollingMode={PollingMode.AutoPoll}
          options={{
            pollIntervalSeconds: parseInt(VITE_CONFIGCAT_POLL_INTERVAL ?? "60"),
            defaultUser,
          }}
        >
          <Provider store={store}>
            <ConfigCatFlagReloader />
          </Provider>
        </ConfigCatProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
}

function ConfigCatFlagReloader(props: any) {
  // const currentHawkeyeUser = useAppSelector((state) => state.userInfo.userInfo);

  return getEnvVariableValue(ENV_VAR.VITE_SHOULD_PERSIST) === "Y" ? (
    <RoutersWithPersist {...props} />
  ) : (
    <Routers {...props} />
  );
}

// Routers component without persist
function Routers() {
  const reduxDispatch = useAppDispatch();

  const currentHawkeyeUser = useAppSelector((state) => state.userInfo.userInfo);

  const org = useAppSelector((state) => state.common.org);

  // ConfigCat user state must be declared before useFeatureFlag usage
  const [configCatUser, setConfigCatUser] = React.useState<ConfigCatUser | null>(null);

  // Use whitelabel hook instead
  const { overrideFavicon } = useIsWhitelabelled();

  // Get overrideLogo feature flag
  const { value: overrideLogo } = useFeatureFlag(
    ConfigCatFeatureFlag.OVERRIDE_LOGO,
    "",
    configCatUser || defaultUser
  );

  const getOrganization = async () => {
    const org = await OrgService.getOrg();
    if (org.status === 200) {
      const orgData: OrgSpec = org.data?.spec;

      reduxDispatch(setOrg(orgData));
    }
  };

  useEffect(() => {
    getOrganization();
  }, []);

  // Use overrideFavicon from whitelabel if valid, else org favicon
  const isValidFavicon = isValidHttpUrl(overrideFavicon || "");
  useFavicon(isValidFavicon ? overrideFavicon : org?.favicon_url);
  useTitleChange(org?.page_title);

  //ConfigCat Manager================================

  //Getting the feature flag from ConfigCat
  const { value: isChangeAPIURLEnabled, loading: isChangeAPIURLEnabledFlagLoading } =
    useFeatureFlag(ConfigCatFeatureFlag.CHANGE_API_URL, true, configCatUser || defaultUser);

  const { value: enabledConnections } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLED_CONNECTIONS,
    "",
    configCatUser || defaultUser
  );

  const { value: isAwsExternalIdAutoGenerationEnabled } = useFeatureFlag(
    ConfigCatFeatureFlag.AUTO_GENERATE_AWS_EXTERNAL_ID,
    true,
    configCatUser || defaultUser
  );

  const { value: isAwsOIDCEnabled } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_AWS_OIDC_FORM,
    false,
    configCatUser || defaultUser
  );

  const { value: isAwsAccessKeyFormEnabled } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_AWS_ACCESS_KEY_FORM,
    false,
    configCatUser || defaultUser
  );

  const { value: isSessionFileUploadEnabled } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_SESSION_FILE_UPLOAD,
    false,
    configCatUser || defaultUser
  );

  const { value: isEnableMSA } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_MSA,
    true,
    configCatUser || defaultUser
  );

  const { value: showTelemetryInSessions } = useFeatureFlag(
    ConfigCatFeatureFlag.SHOW_TELEMETRY_IN_SESSIONS,
    true,
    configCatUser || defaultUser
  );

  const { value: enableAuth0 } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_AUTH0,
    false,
    configCatUser || defaultUser
  );

  const { value: enableSourceTypeSelection } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_SOURCE_TYPE_SELECTION,
    false,
    configCatUser || defaultUser
  );

  const { value: isFileAttachmentMandatory } = useFeatureFlag(
    ConfigCatFeatureFlag.IS_FILE_ATTACHMENT_MANDATORY,
    false,
    configCatUser || defaultUser
  );

  const { value: enableUpdateServices } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_UPDATE_SERVICES,
    false,
    configCatUser || defaultUser
  );

  const { value: hideSessionStarterQuestions } = useFeatureFlag(
    ConfigCatFeatureFlag.HIDE_SESSION_STARTER_QUESTIONS,
    false,
    configCatUser || defaultUser
  );

  const { value: enableSplunkTeleport } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_SPLUNK_TELEPORT,
    false,
    configCatUser || defaultUser
  );

  const { value: enablePrometheusTeleport } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_PROMETHEUS_TELEPORT,
    false,
    configCatUser || defaultUser
  );

  const { value: enableProjectSummary } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_PROJECT_SUMMARY,
    false,
    configCatUser || defaultUser
  );

  const { value: enableInstructionsBeta } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_INSTRUCTIONS_BETA,
    false,
    configCatUser || defaultUser
  );

  const { value: enableAdvancedInstructions } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_ADVANCED_INSTRUCTIONS,
    false,
    configCatUser || defaultUser
  );

  const { value: slackSignupLink } = useFeatureFlag(
    ConfigCatFeatureFlag.SLACK_SIGNUP_LINK,
    "",
    configCatUser || defaultUser
  );

  const { value: enablePublicLinkSharing } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_PUBLIC_LINK_SHARING,
    false,
    configCatUser || defaultUser
  );

  const { value: whitelabeling } = useFeatureFlag(
    ConfigCatFeatureFlag.whitelabeling,
    null,
    configCatUser || defaultUser
  );

  const { value: hideGroupingInstructions } = useFeatureFlag(
    ConfigCatFeatureFlag.HIDE_GROUPING_INSTRUCTIONS,
    false,
    configCatUser || defaultUser
  );

  const { value: enableSplitVisualizeGraph } = useFeatureFlag(
    ConfigCatFeatureFlag.ENABLE_SPLIT_VISUALIZE_GRAPH,
    false,
    configCatUser || defaultUser
  );

  // useEffect to set ConfigCatUser when the user's information changes
  useEffect(() => {
    console.log("currentHawkeyeUser", currentHawkeyeUser);
    if (currentHawkeyeUser?.uuid && currentHawkeyeUser?.email) {
      setConfigCatUser({
        identifier: subDomain,
        email: currentHawkeyeUser?.email,
        custom: {
          baseUrl: AxiosService.getDefaultApiUrl(true),
          browserOrigin: window.location.origin,
          org_name: getEnvVariableValue(ENV_VAR.VITE_ORG_NAME),
          cluster: getEnvVariableValue(ENV_VAR.VITE_CLUSTER),
          user_role: "user",
          firstName: currentHawkeyeUser?.firstName ?? "",
          lastName: currentHawkeyeUser?.lastName ?? "",
          orgUuid: currentHawkeyeUser?.orgUuid ?? "",
          utcTime: new Date().toISOString(),
          localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          localTime: new Date().toString(),
          timeOffset: new Date().getTimezoneOffset(),
          userAgent: navigator.userAgent,
        },
      });
    }
  }, [currentHawkeyeUser, subDomain]);

  // useEffect to set ConfigCat flags when the feature flags change
  useEffect(() => {
    reduxDispatch(
      setFlag({ name: ConfigCatFeatureFlag.CHANGE_API_URL, value: isChangeAPIURLEnabled })
    );
  }, [isChangeAPIURLEnabled]);

  useEffect(() => {
    reduxDispatch(setFlag({ name: ConfigCatFeatureFlag.OVERRIDE_LOGO, value: overrideLogo }));
  }, [overrideLogo]);

  useEffect(() => {
    //Make array from the comma separated string and trim the strings
    const enabledConnectionsAsArray = enabledConnections
      ?.split(",")
      .map((item: string) => item.trim());
    reduxDispatch(
      setFlag({ name: ConfigCatFeatureFlag.ENABLED_CONNECTIONS, value: enabledConnectionsAsArray })
    );
  }, [enabledConnections]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.AUTO_GENERATE_AWS_EXTERNAL_ID,
        value: isAwsExternalIdAutoGenerationEnabled,
      })
    );
  }, [isAwsExternalIdAutoGenerationEnabled]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_SESSION_FILE_UPLOAD,
        value: isSessionFileUploadEnabled,
      })
    );
  }, [isSessionFileUploadEnabled]);

  useEffect(() => {
    reduxDispatch(
      setFlag({ name: ConfigCatFeatureFlag.ENABLE_AWS_OIDC_FORM, value: isAwsOIDCEnabled })
    );
  }, [isAwsOIDCEnabled]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_AWS_ACCESS_KEY_FORM,
        value: isAwsAccessKeyFormEnabled,
      })
    );
  }, [isAwsAccessKeyFormEnabled]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_MSA,
        value: isEnableMSA,
      })
    );
  }, [isEnableMSA]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.SHOW_TELEMETRY_IN_SESSIONS,
        value: showTelemetryInSessions,
      })
    );
  }, [showTelemetryInSessions]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_AUTH0,
        value: enableAuth0,
      })
    );
  }, [enableAuth0]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_SOURCE_TYPE_SELECTION,
        value: enableSourceTypeSelection,
      })
    );
  }, [enableSourceTypeSelection]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.IS_FILE_ATTACHMENT_MANDATORY,
        value: isFileAttachmentMandatory,
      })
    );
  }, [isFileAttachmentMandatory]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_UPDATE_SERVICES,
        value: enableUpdateServices,
      })
    );
  }, [enableUpdateServices]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.HIDE_SESSION_STARTER_QUESTIONS,
        value: hideSessionStarterQuestions,
      })
    );
  }, [hideSessionStarterQuestions]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_SPLUNK_TELEPORT,
        value: enableSplunkTeleport,
      })
    );
  }, [enableSplunkTeleport]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_PROMETHEUS_TELEPORT,
        value: enablePrometheusTeleport,
      })
    );
  }, [enablePrometheusTeleport]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_PROJECT_SUMMARY,
        value: enableProjectSummary,
      })
    );
  }, [enableProjectSummary]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_INSTRUCTIONS_BETA,
        value: enableInstructionsBeta,
      })
    );
  }, [enableInstructionsBeta]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_ADVANCED_INSTRUCTIONS,
        value: enableAdvancedInstructions,
      })
    );
  }, [enableAdvancedInstructions]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.SLACK_SIGNUP_LINK,
        value: slackSignupLink,
      })
    );
  }, [slackSignupLink]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_PUBLIC_LINK_SHARING,
        value: enablePublicLinkSharing,
      })
    );
  }, [enablePublicLinkSharing]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.whitelabeling,
        value: whitelabeling,
      })
    );
  }, [whitelabeling]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.HIDE_GROUPING_INSTRUCTIONS,
        value: hideGroupingInstructions,
      })
    );
  }, [hideGroupingInstructions]);

  useEffect(() => {
    reduxDispatch(
      setFlag({
        name: ConfigCatFeatureFlag.ENABLE_SPLIT_VISUALIZE_GRAPH,
        value: enableSplitVisualizeGraph,
      })
    );
  }, [enableSplitVisualizeGraph]);

  useEffect(() => {
    getVersionInfo();
  }, []);

  const getVersionInfo = async () => {
    const response = await VersionService.getVersion();
    reduxDispatch(setRavenVersionInfo(response.data));
  };

  function applyTheme(mode?: Themes, reduxDispatch?: AppDispatch) {
    const theme = mode ?? (getLocalStorage("theme") as Themes) ?? Themes.SYSTEM;

    if (theme === Themes.DARK) {
      document.documentElement.classList.add("dark");
      setLocalStorage("theme", Themes.DARK);
    } else if (theme === Themes.LIGHT) {
      document.documentElement.classList.remove("dark");
      setLocalStorage("theme", Themes.LIGHT);
    } else if (theme === Themes.SYSTEM) {
      localStorage.removeItem("theme");
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }

    // Dispatch Redux update if reduxDispatch is provided
    if (reduxDispatch) {
      reduxDispatch(setMode(theme));
    }
  }

  useEffect(() => {
    // Apply theme when the app loads
    applyTheme(undefined, reduxDispatch);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    function handleSystemThemeChange() {
      if (!localStorage.getItem("theme")) {
        applyTheme(Themes.SYSTEM, reduxDispatch);
      }
    }
    mediaQuery.addEventListener("change", handleSystemThemeChange);

    // Listen for changes in localStorage (other tabs updating theme)
    function handleStorageChange(event: StorageEvent) {
      if (event.key === "theme") {
        applyTheme(undefined, reduxDispatch);
      }
    }
    window.addEventListener("storage", handleStorageChange);

    // Cleanup event listeners
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Show loading state instead of early return to maintain hooks order
  if (isChangeAPIURLEnabledFlagLoading) {
    return <Loader />;
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <React.Suspense fallback={<Loader />}>
        <DocsBotAIWidget />

        <Routes>
          <Route path="*" element={<Page404 />} />
          <Route path={routes.linkPageBase} element={<PageLogin />} />
          <Route path={routes.linkPageLogin} element={<PageLogin />} />
          <Route path={routes.linkPageSignUp} element={<PageSignUp />} />
          <Route path={routes.linkPageJoin} element={<PageJoinToday />} />
          <Route path={routes.linkPageForgotPassword} element={<PageForgotPassword />} />
          <Route path={routes.linkPageResetPassword} element={<PageResetPassword />} />
          <Route path={"/share/:base64_payload/:signature"} element={<PageShareLink />} />
          <Route path={"/shared/:base64_payload/:signature"} element={<PageShareLink />} />
          {isChangeAPIURLEnabled && (
            <>
              <Route path={routes.linkPageBaseAPIChanger} element={<BaseAPIChanger />} />
              <Route path={routes.linkVersionInfo} element={<PageVersions />} />
            </>
          )}
          <Route path={`${routes.linkAuthBase}/*`} element={<AuthLayout />} />
          <Route path={`${routes.linkAuthRoute}/*`} element={<DefaultLayout />} />
        </Routes>
      </React.Suspense>
    </Router>
  );
}

// Routers component with persist
function RoutersWithPersist(props: any) {
  return (
    <PersistGate persistor={persistor}>
      <Routers {...props} />
    </PersistGate>
  );
}

export default App;
